import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, Users, X } from 'lucide-react';
import logger from '../../utils/logger';

interface TeamDropdownFilterProps {
  teams: any[];
  organizations: any[];
  selectedValue: string; // Either team ID or "organization"
  onSelectionChange?: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  style?: React.CSSProperties;
}

const TeamDropdownFilter: React.FC<TeamDropdownFilterProps> = ({
  teams,
  organizations,
  selectedValue,
  onSelectionChange,
  disabled = false,
  placeholder = "Select Team / Organization",
  style = {}
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Combine teams and organizations into single list with type
  const allOptions = useMemo(() => {
    const options = [
      ...teams.map((team: any) => ({
        id: team._id,
        name: team.name,
        type: 'team' as const
      })),
      ...organizations.map((org: any) => ({
        id: 'organization',
        name: org.name,
        type: 'organization' as const
      }))
    ];
    return options;
  }, [teams, organizations]);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return allOptions;
    return allOptions.filter((option) =>
      option.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allOptions, searchTerm]);

  // Show search box only if more than 10 items
  const showSearch = allOptions.length > 10;

  // Handle selection
  const handleSelect = (value: string) => {
    if (onSelectionChange) {
      onSelectionChange(value);
    }
    setIsOpen(false);
    setSearchTerm('');
    logger.log('👥 Team/Org selection changed:', value);
  };

  // Clear selection
  const handleClear = () => {
    if (onSelectionChange) {
      onSelectionChange('');
    }
    logger.log('👥 Cleared team/org selection');
  };

  // Get display text for the dropdown button
  const getDisplayText = () => {
    if (!selectedValue) return placeholder;

    const selected = allOptions.find(opt => opt.id === selectedValue);
    if (selected) {
      return `${selected.name} - ${selected.type === 'team' ? 'Team' : 'Organization'}`;
    }

    return placeholder;
  };

  // Default styles matching existing dashboard filters
  const defaultStyle = {
    backgroundColor: "white",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    height: "36px",
    fontSize: "14px",
    fontWeight: "500",
    color: "#334155",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
    cursor: disabled ? "not-allowed" : "pointer",
    minWidth: "200px",
    position: "relative" as const,
    ...style
  };

  return (
    <div style={{ position: 'relative' }} ref={dropdownRef}>
      {/* Dropdown Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{
          ...defaultStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 12px',
          border: isOpen ? '1px solid #3b82f6' : defaultStyle.border,
          opacity: disabled ? 0.5 : 1
        }}
        onMouseEnter={(e) => {
          if (!disabled && !isOpen) {
            e.currentTarget.style.borderColor = '#3b82f6';
          }
        }}
        onMouseLeave={(e) => {
          if (!isOpen) {
            e.currentTarget.style.borderColor = '#e2e8f0';
          }
        }}
      >
        <div className="d-flex align-items-center" style={{ minWidth: 0, flex: 1 }}>
          <Users size={16} style={{ marginRight: '8px', color: '#6b7280', flexShrink: 0 }} />
          <span style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: selectedValue ? '#334155' : '#6b7280'
          }}>
            {getDisplayText()}
          </span>
          {selectedValue && (
            <span
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
              style={{
                marginLeft: '8px',
                padding: '2px',
                background: 'none',
                color: '#6b7280',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                borderRadius: '2px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <X size={12} />
            </span>
          )}
        </div>
        <ChevronDown
          size={16}
          style={{
            color: '#6b7280',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
            flexShrink: 0
          }}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            zIndex: 1050,
            marginTop: '4px',
            maxHeight: '500px', // BIGGER - show more items without scroll
            overflowY: 'auto',
            minWidth: '100%'
          }}
        >
          {/* Search Input - Only show if > 10 items */}
          {showSearch && (
            <div style={{ padding: '12px', borderBottom: '1px solid #f1f5f9' }}>
              <input
                type="text"
                placeholder="Search teams..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#e2e8f0';
                }}
              />
            </div>
          )}

          {/* Options List */}
          <div style={{ maxHeight: showSearch ? '420px' : '460px', overflowY: 'auto' }}>
            {filteredOptions.length === 0 ? (
              <div style={{
                padding: '12px',
                textAlign: 'center',
                color: '#6b7280',
                fontSize: '13px'
              }}>
                {searchTerm ? 'No teams match your search' : 'No teams available'}
              </div>
            ) : (
              filteredOptions.map((option) => (
                <div
                  key={option.id}
                  onClick={() => handleSelect(option.id)}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: '1px solid #f1f5f9',
                    backgroundColor: selectedValue === option.id ? '#eff6ff' : 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedValue !== option.id) {
                      e.currentTarget.style.backgroundColor = '#f8fafc';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor =
                      selectedValue === option.id ? '#eff6ff' : 'transparent';
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '13px',
                      fontWeight: '500',
                      color: '#334155',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {option.name}
                    </div>
                    <div style={{
                      fontSize: '11px',
                      color: '#6b7280'
                    }}>
                      {option.type === 'team' ? 'Team' : 'Organization'}
                    </div>
                  </div>
                  {selectedValue === option.id && (
                    <div style={{
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      backgroundColor: '#3b82f6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '10px',
                      flexShrink: 0
                    }}>
                      ✓
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamDropdownFilter;
